import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import { SearchPill, type SearchPillSummary } from "@ui/components/SearchPill";
import { TopBarMobile } from "@ui/components/TopBar/TopBarMobile";
import { Button } from "@ui/primitives/Button";
import type { ReactNode } from "react";
import { useExploreFilters } from "@/layouts/components/explore-filters-context";
import { DEFAULT_EXPLORE_SECTION, sectionForPath } from "@/layouts/components/explore-sections";
import { parseMarketplaceQuery, parseWhereParams } from "@/lib/search-params";

export interface ExploreSearchTopBarProps {
  /** Current path with query stripped — picks the active section. */
  currentPath: string;
  /** Current path with the query string — reads the applied-search summary for the pill. */
  currentUrl: string;
  /** Opens the mobile Explore search overlay (pill press). */
  onOpenSearch: () => void;
  /** Notifications action shown in the right slot on the Explore home (where no filters button sits).
   *  Omitted for signed-out viewers, leaving the slot empty (but still reserved). */
  notificationsAction?: ReactNode;
  /** Forwarded to `TopBarMobile`. Sections whose page never scrolls the frame (a full-bleed map
   *  behind a results sheet) pass `"always"`, since a scroll-driven gradient would never appear. */
  shadow?: "scroll" | "always";
}

/**
 * The search-centric mobile top bar for the Explore home and its browse sections: the logo +
 * a search pill + an optional filters button. The sections are their own bottom tabs, not children
 * of the home, so the leading slot is always the logo — never a back arrow.
 * Pressing the pill opens the search overlay (via `onOpenSearch`). The pill shows the applied-search
 * summary (read from the URL) or the section placeholder. On the home the right slot holds the
 * notifications action; on filterable sections it holds the filters button (from
 * `ExploreFiltersContext`); otherwise it stays empty but reserved.
 */
export function ExploreSearchTopBar({
  currentPath,
  currentUrl,
  onOpenSearch,
  notificationsAction,
  shadow,
}: ExploreSearchTopBarProps) {
  const section = sectionForPath(currentPath);
  const isHome = section === undefined;
  const pillSection = section ?? DEFAULT_EXPLORE_SECTION;
  const filters = useExploreFilters();

  // No applied search on the Explore home; on a section page read it back from the URL. Where
  // sections show "{Section} in {place}" (matching the Figma applied pill); marketplace shows the query.
  let summary: SearchPillSummary | null = null;
  if (!isHome) {
    if (pillSection.searchKind === "where") {
      const where = parseWhereParams(currentUrl);
      if (where) {
        summary = {
          primary: t("components.frames.app_layout.explore_search.summary_where", {
            section: t(pillSection.labelKey),
            place: where.selection.name,
          }),
        };
      }
    } else {
      const query = parseMarketplaceQuery(currentUrl);
      if (query) summary = { primary: query };
    }
  }

  // Home shows a generic "Search"; section pages name what's searched.
  const placeholder = isHome
    ? t("components.frames.app_layout.explore_search.placeholder_default")
    : t(pillSection.placeholderKey);

  return (
    <TopBarMobile
      root
      shadow={shadow}
      centerContent={<SearchPill placeholder={placeholder} summary={summary} onPress={onOpenSearch} />}
      actions={
        filters ? (
          <Button
            aria-label={t("components.frames.app_layout.explore_search.filters_aria_label")}
            className="flex size-9 cursor-pointer items-center justify-center rounded-full text-neutral-600 outline-none hover:bg-neutral-50 hover:text-neutral-900 focus-visible:ring-2 focus-visible:ring-neutral-400"
            onPress={filters.openFilters}
          >
            <Icon name="filter" size="20" />
          </Button>
        ) : isHome ? (
          notificationsAction
        ) : undefined
      }
    />
  );
}
